import React from 'react'

import { useModalContext } from '@fourthwall/components'

import { useCurrentShopQuery } from '../../../../hooks/useCurrentShopQuery'
import type { PaymentMethodItem } from '../../PaymentMethods.types'
import { ConfirmRemovePaymentMethodModal } from '../../components/ConfirmRemovePaymentMethodModal/ConfirmRemovePaymentMethodModal'
import { SelectNewDefaultPaymentMethod } from '../../components/SelectNewDefaultPaymentMethod/SelectNewDefaultPaymentMethod'
import { useShopPaymentMethods } from '../../hooks/useShopPaymentMethods'
import { useRemoveShopPaymentMethodMutation } from '../../queries/useRemoveShopPaymentMethodMutation'
import { useSetShopDefaultPaymentMethodMutation } from '../../queries/useSetShopDefaultPaymentMethodMutation'
import { ShopPaymentMethodsView } from './ShopPaymentMethodsView'
import { getErrorBanner } from './utils/getErrorBanner'

export const ShopPaymentMethodsContainer = () => {
  const { open } = useModalContext()
  const { shopPaymentMethods, invalidateShopPaymentMethods } = useShopPaymentMethods()
  const { removeShopPaymentMethodMutation } = useRemoveShopPaymentMethodMutation()
  const { setShopDefaultPaymentMethodMutation } = useSetShopDefaultPaymentMethodMutation()
  const { invalidateCurrentShopQuery } = useCurrentShopQuery()

  const setAsDefault = (shopPaymentMethodId: string) => {
    setShopDefaultPaymentMethodMutation.mutate([shopPaymentMethodId], {
      onSuccess: () => {
        invalidateShopPaymentMethods()
        invalidateCurrentShopQuery()
      },
    })
  }

  const getDropdownItemsProps = (
    paymentMethod: NonNullable<
      ReturnType<typeof useShopPaymentMethods>['shopPaymentMethods']
    >[number],
  ) => {
    const dropdownItems: PaymentMethodItem['dropdownItemsProps'] = []

    if (!paymentMethod.default) {
      dropdownItems.push({
        label: 'Set as default',
        onClick: () => setAsDefault(paymentMethod.id),
      })
    }

    dropdownItems.push({
      label: 'Remove',
      onClick: () => handleRemove(paymentMethod),
    })

    return dropdownItems
  }

  const removeShopPaymentMethod = (id: string) => {
    removeShopPaymentMethodMutation.mutate([id], {
      onSuccess: () => {
        invalidateShopPaymentMethods()
      },
    })
  }

  const handleRemove = (
    paymentMethod: NonNullable<
      ReturnType<typeof useShopPaymentMethods>['shopPaymentMethods']
    >[number],
  ) => {
    if (!shopPaymentMethods) return

    if (paymentMethod.default && shopPaymentMethods.length > 1) {
      return open(SelectNewDefaultPaymentMethod, {
        removedPaymentMethod: paymentMethod,
        remainingPaymentMethods: shopPaymentMethods.filter((method) => !method.default),
        onSubmit: ({ defaultPaymentMethodId }) => {
          removeShopPaymentMethod(paymentMethod.id)

          if (defaultPaymentMethodId) {
            setAsDefault(defaultPaymentMethodId)
          }
        },
      })
    }

    open(ConfirmRemovePaymentMethodModal, {
      paymentMethodProps: paymentMethod,
      onConfirm: () => {
        removeShopPaymentMethod(paymentMethod.id)
      },
    })
  }

  const paymentMethodsProps =
    shopPaymentMethods?.map((method) => ({
      ...method,
      dropdownItemsProps: getDropdownItemsProps(method),
    })) || []

  if (!shopPaymentMethods?.length) {
    return null
  }

  return (
    <ShopPaymentMethodsView
      paymentMethodsProps={paymentMethodsProps}
      errorBanner={getErrorBanner(paymentMethodsProps)}
    />
  )
}
